import {
  findQuestionByKey,
  type InspectionAnswerValue,
  type InspectionQuestion,
  type InspectionSchema,
} from '@sitewise/shared';
import { useEffect, useRef } from 'react';

import { upsertInspectionAnswer, type InspectionAnswerSummary } from '../../../api/inspections.js';
import {
  buildPrefillValue,
  countChecklistDefects,
  countResolvedDefectWorkOrders,
  footerSyncTargets,
  formatInspectionDate,
  type PrefillContext,
} from './form-helpers.js';

function questionNeedsPrefill(
  question: InspectionQuestion,
  answersById: Map<string, InspectionAnswerSummary>,
): boolean {
  if (question.kind !== 'short_text' || !question.prefillSource) return false;
  const existing = answersById.get(question.id);
  return !existing?.value;
}

export function useInspectionPrefill(args: {
  enabled: boolean;
  inspectionId: string;
  schema: InspectionSchema;
  answers: InspectionAnswerSummary[];
  ctx: PrefillContext;
}): void {
  const ran = useRef(false);
  const { enabled, inspectionId, schema, answers, ctx } = args;

  useEffect(() => {
    if (!enabled || ran.current) return;
    const answersById = new Map(answers.map((a) => [a.questionId, a]));
    const tasks: Array<{ questionId: string; value: InspectionAnswerValue }> = [];

    for (const section of schema.sections) {
      for (const question of section.questions) {
        if (!questionNeedsPrefill(question, answersById)) continue;
        const value = buildPrefillValue(question, ctx);
        if (value) tasks.push({ questionId: question.id, value });
      }
    }

    if (tasks.length === 0) {
      ran.current = true;
      return;
    }

    ran.current = true;
    void (async () => {
      for (const task of tasks) {
        await upsertInspectionAnswer(inspectionId, task.questionId, {
          value: task.value as never,
          notes: null,
          photoAttachmentIds: [],
        });
      }
    })();
  }, [enabled, inspectionId, schema, answers, ctx]);
}

export function useFooterFieldSync(args: {
  enabled: boolean;
  inspectionId: string;
  schema: InspectionSchema;
  answers: InspectionAnswerSummary[];
  onSynced?: () => void;
}): void {
  const { enabled, inspectionId, schema, answers, onSynced } = args;
  const syncing = useRef(false);

  useEffect(() => {
    if (!enabled || syncing.current) return;
    const targets = footerSyncTargets(schema);
    if (!targets.repairsNeededId && !targets.workOrdersSubmittedId) return;

    const checklistDefects = countChecklistDefects(schema, answers);
    const resolvedWoCount = countResolvedDefectWorkOrders(answers);
    const answersById = new Map(answers.map((a) => [a.questionId, a]));
    const patches: Array<{ questionId: string; value: InspectionAnswerValue }> = [];

    if (targets.repairsNeededId) {
      const current = answersById.get(targets.repairsNeededId)?.value;
      const desired: InspectionAnswerValue = {
        kind: 'yes_no',
        value: checklistDefects > 0 ? 'yes' : 'no',
      };
      if (
        current?.kind !== 'yes_no' ||
        current.value !== desired.value
      ) {
        patches.push({ questionId: targets.repairsNeededId, value: desired });
      }
    }

    if (targets.workOrdersSubmittedId) {
      const current = answersById.get(targets.workOrdersSubmittedId)?.value;
      const desired: InspectionAnswerValue = {
        kind: 'yes_no',
        value: resolvedWoCount > 0 ? 'yes' : 'no',
      };
      if (current?.kind !== 'yes_no' || current.value !== desired.value) {
        patches.push({ questionId: targets.workOrdersSubmittedId, value: desired });
      }
    }

    if (targets.woDateSubmittedId && resolvedWoCount > 0) {
      const current = answersById.get(targets.woDateSubmittedId)?.value;
      const today = formatInspectionDate(new Date().toISOString()) ?? '';
      const desired: InspectionAnswerValue = { kind: 'short_text', value: today };
      if (current?.kind !== 'short_text' || current.value !== today) {
        patches.push({ questionId: targets.woDateSubmittedId, value: desired });
      }
    }

    if (patches.length === 0) return;

    syncing.current = true;
    void (async () => {
      try {
        for (const patch of patches) {
          await upsertInspectionAnswer(inspectionId, patch.questionId, {
            value: patch.value as never,
            notes: null,
            photoAttachmentIds: [],
          });
        }
        onSynced?.();
      } finally {
        syncing.current = false;
      }
    })();
  }, [enabled, inspectionId, schema, answers, onSynced]);
}

export function buildPrefillContext(args: {
  building: PrefillContext['building'];
  unitNumber: string | null;
  inspectorName: string | null;
  inspectionDateIso: string | null;
}): PrefillContext {
  return {
    building: args.building,
    unitNumber: args.unitNumber,
    inspectorName: args.inspectorName,
    inspectionDate:
      formatInspectionDate(args.inspectionDateIso) ??
      formatInspectionDate(new Date().toISOString()),
  };
}

export function findCommentsQuestionId(schema: InspectionSchema): string | null {
  return findQuestionByKey(schema, 'footer.comments')?.id ?? null;
}
